import React from 'react'

import {
  ActionIcon,
  Button,
  Divider,
  Group,
  Space,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core'
import { observer } from 'mobx-react-lite'

import {
  useTrackedChangeStatus,
  useTrackedChangeThread,
} from '#components/ChangeTracking/hooks'
import { FaIcon } from '#components/FaIcon'
import { Toast } from '#components/Toast'
import { useMst } from '#hooks'
import { ILoadedScript, ITrackedChange } from '#state'
import { nido } from '#util'

import * as CommentParts from '../Comments/CommentParts'

import { CardWrapper } from './CardWrapper'
import * as Parts from './Parts'
import { TrackedChangeReply } from './types'

import styles from './Thread.module.css'

export type ThreadBaseProps = {
  thread: ITrackedChange
  selected?: boolean
  onUpdateReply?: (id: string, text: string) => void
  onDeleteReply?: (id: string) => void
  script: ILoadedScript
}

const PLACEHOLDER = 'something'

const ThreadReply = observer(function ThreadReply({
  reply,
  isEditable,
  operations,
}: {
  selected?: boolean
  reply: TrackedChangeReply
  isEditable?: boolean
  operations: {
    onSubmit: Callback<[string]>
    onDelete?: Callback
  }
}) {
  const [isEditing, setIsEditing] = React.useState(false)
  const [text, setText] = React.useState(reply.text)

  const reset = () => {
    setText(reply.text)
    setIsEditing(false)
  }

  return (
    <Stack gap={5}>
      <Group justify="space-between" wrap="nowrap" gap={5}>
        <Parts.Attribution
          name={reply.creator.name}
          avatar={reply.creator.avatar}
          timestamp={reply.createdAt}
        />
      </Group>
      {isEditing && (
        <CommentParts.EditCommentForm
          text={text}
          setText={setText}
          initialValue={reply.text}
          onSubmit={async (val) => {
            operations.onSubmit(val)
            setIsEditing(false)
          }}
          onCancel={reset}
        />
      )}
      {!isEditing && (
        <Group align="flex-start" justify="space-between" wrap="nowrap" gap={5}>
          <Text px={5}>
            <CommentParts.CommentText>{reply.text}</CommentParts.CommentText>
          </Text>
          {isEditable && (
            <CommentParts.EditMenu
              onClickEdit={() => setIsEditing(true)}
              onClickDelete={operations.onDelete}
            />
          )}
        </Group>
      )}
    </Stack>
  )
})

export const ThreadBase = observer<ThreadBaseProps>(function ThreadBase({
  script,
  selected,
  thread,
  onDeleteReply,
  onUpdateReply,
}) {
  const mst = useMst()
  const [dummyText, setDummyText] = React.useState('')

  const trackedChangeStatus = useTrackedChangeStatus(script, thread)
  const { scrollToTrackedChangeMark, selectTrackedChange } =
    useTrackedChangeThread(script)

  const onClick = () => {
    selectTrackedChange(thread.id)
    scrollToTrackedChangeMark(thread.id, thread.changeType === 'addition')
  }

  const createReplyMutation = nido.scripts.createTrackedChangeReply.useMutation(
    {
      onSuccess() {
        script.trackEvent('SCRIPT_TRACKED_CHANGE_UNRESOLVED')
        setDummyText('')
      },
    },
  )

  async function onCreateReply() {
    await createReplyMutation.mutateAsync({
      params: { scriptId: script.id, changeId: thread.id },
      body: { text: dummyText },
    })
  }

  return (
    <CardWrapper selected={selected} onClick={onClick}>
      <Stack gap={8}>
        <Group align="flex-start" justify="space-between" wrap="nowrap" gap={5}>
          <Parts.Attribution
            name={thread.creator.name}
            avatar={thread.creator.avatar}
            timestamp={thread.createdAt}
          />
          <Group gap={0}>
            {trackedChangeStatus.canApprove && (
              <Tooltip label="Approve">
                <ActionIcon
                  className={styles.action}
                  onClick={trackedChangeStatus.approve}
                  disabled={trackedChangeStatus.isPending}
                >
                  <FaIcon icon="fa-check" />
                </ActionIcon>
              </Tooltip>
            )}
            {trackedChangeStatus.canReject && (
              <Tooltip label="Decline">
                <ActionIcon
                  className={styles.action}
                  onClick={trackedChangeStatus.reject}
                  disabled={trackedChangeStatus.isPending}
                >
                  <FaIcon icon="fa-ban" />
                </ActionIcon>
              </Tooltip>
            )}
            {trackedChangeStatus.canDelete && (
              <Tooltip label="Delete">
                <ActionIcon
                  className={styles.action___delete}
                  onClick={trackedChangeStatus.delete}
                  disabled={trackedChangeStatus.isPending}
                >
                  <FaIcon c="red" icon="fa-trash" />
                </ActionIcon>
              </Tooltip>
            )}
            {trackedChangeStatus.canUnresolve && (
              <Button
                fz={14}
                p={8}
                size="xs"
                variant="subtle"
                onClick={trackedChangeStatus.unresolve}
                disabled={trackedChangeStatus.isPending}
              >
                Unresolve
              </Button>
            )}
          </Group>
        </Group>
        {trackedChangeStatus.hasError && (
          <Toast
            message="We weren't able to review the tracked change"
            icon="fas fa-circle-exclamation"
            type="error"
            dismissable={true}
            onDismiss={trackedChangeStatus.resetError}
          />
        )}
        <Parts.Snippet
          text={thread.latestSnippet ?? PLACEHOLDER}
          isAddition={thread.changeType === 'addition'}
        />
        <Space />
        <Parts.ReviewerAttribution thread={thread} />
        {thread.replies?.map((r) => (
          <React.Fragment key={r.id}>
            <Divider mt={5} mb={5} pr={5} />
            <ThreadReply
              key={r.id}
              reply={r}
              isEditable={mst.user.id === r.creator.id}
              operations={{
                onSubmit: (val) => onUpdateReply?.(r.id, val),
                onDelete: () => onDeleteReply?.(r.id),
              }}
            />
          </React.Fragment>
        ))}
        {selected && (
          <CommentParts.NewCommentForm
            mode="existing-thread"
            text={dummyText}
            setText={setDummyText}
            placeholder="Reply..."
            avatar={mst.user.avatar}
            onSubmit={onCreateReply}
            onCancel={() => setDummyText('')}
          />
        )}
      </Stack>
    </CardWrapper>
  )
})
